import { useState } from 'react';
import {
  Image,
  Upload,
  Search,
  Grid,
  List,
  Trash2,
  Download,
  Copy,
  X,
  File,
  Film,
  FileText,
  Folder,
  MoreVertical,
  Eye,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface MediaFile {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document' | 'other';
  url: string;
  size: number;
  dimensions?: { width: number; height: number };
  uploadedAt: Date;
  uploadedBy: string;
}

// Demo data
const initialMedia: MediaFile[] = [
  {
    id: '1',
    name: 'hero-background.jpg',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800',
    size: 2456789,
    dimensions: { width: 1920, height: 1080 },
    uploadedAt: new Date('2024-01-15'),
    uploadedBy: 'Admin',
  },
  {
    id: '2',
    name: 'team-photo.jpg',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800',
    size: 1234567,
    dimensions: { width: 1600, height: 900 },
    uploadedAt: new Date('2024-01-14'),
    uploadedBy: 'Admin',
  },
  {
    id: '3',
    name: 'product-demo.mp4',
    type: 'video',
    url: '#',
    size: 15678900,
    uploadedAt: new Date('2024-01-12'),
    uploadedBy: 'Admin',
  },
  {
    id: '4',
    name: 'whitepaper.pdf',
    type: 'document',
    url: '#',
    size: 456789,
    uploadedAt: new Date('2024-01-10'),
    uploadedBy: 'Admin',
  },
  {
    id: '5',
    name: 'services-icon.png',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    size: 123456,
    dimensions: { width: 512, height: 512 },
    uploadedAt: new Date('2024-01-08'),
    uploadedBy: 'Admin',
  },
  {
    id: '6',
    name: 'blog-cover.jpg',
    type: 'image',
    url: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=800',
    size: 987654,
    dimensions: { width: 1200, height: 630 },
    uploadedAt: new Date('2024-01-05'),
    uploadedBy: 'Admin',
  },
];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (type: string) => {
  switch (type) {
    case 'image': return <Image size={24} className="text-green-500" />;
    case 'video': return <Film size={24} className="text-purple-500" />;
    case 'document': return <FileText size={24} className="text-blue-500" />;
    default: return <File size={24} className="text-gray-500" />;
  }
};

export function AdminMedia() {
  const [media, setMedia] = useState<MediaFile[]>(initialMedia);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Filter media
  const filteredMedia = media.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || file.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleDelete = (id: string) => {
    setMedia(media.filter(f => f.id !== id));
    setShowDeleteConfirm(null);
    if (selectedFile?.id === id) setSelectedFile(null);
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    // Could add toast notification here
  };

  const stats = {
    total: media.length,
    images: media.filter(f => f.type === 'image').length,
    videos: media.filter(f => f.type === 'video').length,
    documents: media.filter(f => f.type === 'document').length,
    totalSize: media.reduce((acc, f) => acc + f.size, 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Medya Kutuphanesi</h1>
          <p className="text-text-secondary">Gorselleri, videolari ve dosyalari yonetin</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors">
          <Upload size={18} /> Dosya Yukle
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{stats.total}</p>
          <p className="text-sm text-text-secondary">Toplam Dosya</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">{stats.images}</p>
          <p className="text-sm text-text-secondary">Gorsel</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-500">{stats.videos}</p>
          <p className="text-sm text-text-secondary">Video</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-500">{stats.documents}</p>
          <p className="text-sm text-text-secondary">Belge</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-text-primary">{formatFileSize(stats.totalSize)}</p>
          <p className="text-sm text-text-secondary">Toplam Boyut</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Dosya ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">Tum Tipler</option>
            <option value="image">Gorseller</option>
            <option value="video">Videolar</option>
            <option value="document">Belgeler</option>
          </select>
          <div className="flex border border-border-default rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-2 transition-colors',
                viewMode === 'grid' ? 'bg-accent text-white' : 'bg-bg-card text-text-secondary hover:text-text-primary'
              )}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'p-2 transition-colors',
                viewMode === 'list' ? 'bg-accent text-white' : 'bg-bg-card text-text-secondary hover:text-text-primary'
              )}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Media Grid/List */}
        <div className="flex-1">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMedia.map(file => (
                <div
                  key={file.id}
                  onClick={() => setSelectedFile(file)}
                  className={cn(
                    'bg-bg-card border rounded-xl overflow-hidden cursor-pointer transition-all',
                    selectedFile?.id === file.id ? 'border-accent ring-2 ring-accent/20' : 'border-border-default hover:border-accent/50'
                  )}
                >
                  <div className="aspect-square bg-bg-elevated flex items-center justify-center overflow-hidden">
                    {file.type === 'image' ? (
                      <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                      getFileIcon(file.type)
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
                    <p className="text-xs text-text-tertiary">{formatFileSize(file.size)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-bg-elevated">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase">Dosya</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase">Tip</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase">Boyut</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase">Tarih</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-text-tertiary uppercase">Islemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-default">
                  {filteredMedia.map(file => (
                    <tr
                      key={file.id}
                      onClick={() => setSelectedFile(file)}
                      className={cn(
                        'cursor-pointer transition-colors',
                        selectedFile?.id === file.id ? 'bg-accent/5' : 'hover:bg-bg-elevated/50'
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {file.type === 'image' ? (
                            <img src={file.url} alt={file.name} className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-bg-elevated flex items-center justify-center">
                              {getFileIcon(file.type)}
                            </div>
                          )}
                          <span className="text-sm text-text-primary">{file.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'px-2 py-1 rounded-full text-xs',
                          file.type === 'image' ? 'bg-green-500/10 text-green-500' :
                          file.type === 'video' ? 'bg-purple-500/10 text-purple-500' :
                          'bg-blue-500/10 text-blue-500'
                        )}>
                          {file.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">{formatFileSize(file.size)}</td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {file.uploadedAt.toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCopyUrl(file.url); }}
                            className="p-1.5 rounded hover:bg-bg-elevated text-text-tertiary hover:text-accent"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(file.id); }}
                            className="p-1.5 rounded hover:bg-red-500/10 text-text-tertiary hover:text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredMedia.length === 0 && (
            <div className="text-center py-12 bg-bg-card border border-border-default rounded-xl">
              <Folder className="mx-auto mb-4 text-text-tertiary" size={48} />
              <p className="text-text-tertiary">Dosya bulunamadi</p>
            </div>
          )}
        </div>

        {/* File Details Sidebar */}
        {selectedFile && (
          <div className="w-80 bg-bg-card border border-border-default rounded-xl p-6 h-fit sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-text-primary">Dosya Detaylari</h3>
              <button
                onClick={() => setSelectedFile(null)}
                className="p-1 rounded hover:bg-bg-elevated text-text-tertiary"
              >
                <X size={18} />
              </button>
            </div>

            {selectedFile.type === 'image' ? (
              <img
                src={selectedFile.url}
                alt={selectedFile.name}
                className="w-full aspect-video object-cover rounded-lg mb-4"
              />
            ) : (
              <div className="w-full aspect-video bg-bg-elevated rounded-lg flex items-center justify-center mb-4">
                {getFileIcon(selectedFile.type)}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <p className="text-xs text-text-tertiary mb-1">Dosya Adi</p>
                <p className="text-sm text-text-primary font-medium">{selectedFile.name}</p>
              </div>
              <div>
                <p className="text-xs text-text-tertiary mb-1">Tip</p>
                <p className="text-sm text-text-primary capitalize">{selectedFile.type}</p>
              </div>
              <div>
                <p className="text-xs text-text-tertiary mb-1">Boyut</p>
                <p className="text-sm text-text-primary">{formatFileSize(selectedFile.size)}</p>
              </div>
              {selectedFile.dimensions && (
                <div>
                  <p className="text-xs text-text-tertiary mb-1">Boyutlar</p>
                  <p className="text-sm text-text-primary">
                    {selectedFile.dimensions.width} x {selectedFile.dimensions.height} px
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-text-tertiary mb-1">Yuklenme Tarihi</p>
                <p className="text-sm text-text-primary">
                  {selectedFile.uploadedAt.toLocaleDateString('tr-TR')}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-tertiary mb-1">Yukleyen</p>
                <p className="text-sm text-text-primary">{selectedFile.uploadedBy}</p>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border-default space-y-2">
              <button
                onClick={() => handleCopyUrl(selectedFile.url)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-bg-elevated text-text-primary hover:bg-accent hover:text-white transition-colors"
              >
                <Link2 size={16} /> URL Kopyala
              </button>
              <button
                onClick={() => setShowDeleteConfirm(selectedFile.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-500/20 text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 size={16} /> Sil
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text-primary mb-2">Dosyayi Sil</h3>
            <p className="text-text-secondary mb-6">
              Bu dosyayi silmek istediginizden emin misiniz? Bu islem geri alinamaz.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary transition-colors"
              >
                Iptal
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
